import { QuantitySelectorComponent } from '@theme/component-quantity-selector';
import { getCartLineErrorElements, getCartLineRowFromInput } from '@theme/cart-line-errors';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * @extends {QuantitySelectorComponent}
 */
class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * Updates button states based on current value and limits
   * Cart buttons are always managed client-side, never server-disabled
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();
    const atMax = effectiveMax !== null && value >= effectiveMax;

    minusButton.disabled = value <= min;
    // Keep + clickable at max so we can show inline feedback on click
    plusButton.disabled = false;
    plusButton.classList.toggle('is-at-max', atMax);
    plusButton.setAttribute('aria-disabled', atMax ? 'true' : 'false');

    if (!atMax) {
      this.#clearQuantityLimitMessage();
    }
  }

  /**
   * Handles the quantity increase event.
   * @param {Event} event - The event.
   */
  increaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;
    event.preventDefault();

    const { value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    if (effectiveMax !== null && value >= effectiveMax) {
      this.#showQuantityLimitMessage();
      return;
    }

    this.updateQuantity(1);
  }

  /**
   * Handles the quantity set event (on blur).
   * Validates and snaps to valid values.
   * @param {Event} event - The event.
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLInputElement)) return;

    event.preventDefault();
    const { quantityInput } = this.refs;
    const { min, step } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();
    const requested = parseInt(event.target.value) || 0;

    if (effectiveMax !== null && requested > effectiveMax) {
      this.#showQuantityLimitMessage();
    }

    const quantity = Math.min(effectiveMax ?? Infinity, Math.max(min, requested));

    if ((quantity - min) % step !== 0) {
      quantityInput.value = quantity.toString();
      quantityInput.reportValidity();
      return;
    }

    quantityInput.value = quantity.toString();
    this.onQuantityChange();
    this.updateButtonStates();
  }

  /**
   * @returns {number | null}
   */
  #getCartLine() {
    const line = Number(this.refs.quantityInput?.dataset.cartLine);
    return Number.isFinite(line) && line > 0 ? line : null;
  }

  /**
   * @returns {{ errorText: HTMLElement | null, errorContainer: HTMLElement | null }}
   */
  #getLineErrorElements() {
    const line = this.#getCartLine();
    if (!line) return { errorText: null, errorContainer: null };

    const row = getCartLineRowFromInput(this.refs.quantityInput);
    return getCartLineErrorElements(row, line);
  }

  #showQuantityLimitMessage() {
    const message = this.dataset.maxQuantityMessage;
    if (!message) return;

    const { errorText, errorContainer } = this.#getLineErrorElements();
    if (!errorText || !errorContainer) return;

    errorText.textContent = message;
    errorContainer.classList.remove('hidden');
  }

  #clearQuantityLimitMessage() {
    const { errorText, errorContainer } = this.#getLineErrorElements();
    if (errorText) errorText.textContent = '';
    errorContainer?.classList.add('hidden');
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
